import React, { useRef } from 'react';
import './QuemEhOFinn.css';
import inteiro from '../assets/finn-inteiro.png';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

/**
 * Quem é o Finn — a apresentação, quando ela toca no avatar.
 *
 * ── Por que ele existe ────────────────────────────────────────────────────
 *
 * Ela pediu, e o pedido tem razão: uma capivara aparece falando do dinheiro
 * dela e não há lugar nenhum que explique quem é. O nome ao lado do balão diz
 * COMO ele se chama; isto diz o que ele faz e o que ele não faz.
 *
 * ── A arte precisou ser limpa ────────────────────────────────────────────
 *
 * O PNG original não tinha alpha: o fundo quadriculado estava pintado dentro
 * da imagem. Aqui isso apareceria em toda a volta do bicho, porque 65% do
 * quadro é fundo. `scripts/limpar-fundo-png.py` resolve, e o resultado é o
 * que está em `assets/finn-inteiro.png`.
 *
 * ── As palavras são dela ─────────────────────────────────────────────────
 *
 * Saíram da folha de personagem e de `Finn_Estrutura_Produto.md`, não de uma
 * descrição inventada aqui. O princípio central em especial — "ele não diz o
 * que fazer com o seu dinheiro" — é a regra que separa este app dos outros, e
 * reescrevê-la com palavras minhas a enfraqueceria.
 *
 * ── As três coisas são VERDADE, não promessa ─────────────────────────────
 *
 * Avisar, guardar o objetivo e responder são as três que ele faz de fato
 * hoje. Uma lista de intenções aqui seria a pior mentira possível: dita pela
 * mascote, na primeira vez que alguém pergunta quem ela é.
 */

const FAZ = [
  {
    titulo: 'Te avisa quando algo sai do comum',
    texto: 'O cartão chegando no limite, o mês apertando, uma categoria que disparou. '
      + 'Ele deixa o recado aqui e espera, e só toca o seu celular quando vale a pena.',
  },
  {
    titulo: 'Guarda o que você disse que queria',
    texto: 'Não para cobrar. Para lembrar no momento em que aquilo passa a caber: '
      + 'quando uma parcela acaba e sobra dinheiro que antes não sobrava.',
  },
  {
    titulo: 'Responde quando você pergunta',
    texto: '"Quanto posso gastar esse mês?", "posso comprar isso?". '
      + 'Todo número da resposta é calculado, nunca escrito pelo modelo.',
  },
];

const QuemEhOFinn: React.FC<{ onFechar: () => void }> = ({ onFechar }) => {
  useTravarScroll();
  /* O voltar do celular fecha isto antes de sair do app. */
  useFechavelComVoltar(true, onFechar);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  return (
    <div
      className="qf-fundo"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qf-titulo"
      /*
       * Clicar fora fecha. Diferente do modal da porta, que não tem saída sem
       * resposta: aquele PERGUNTA alguma coisa; este só conta. Prender alguém
       * numa apresentação seria o oposto do "sem julgamentos".
       */
      onClick={onFechar}
    >
      <div className="qf-caixa" ref={caixa} onClick={e => e.stopPropagation()}>
        <button className="qf-fechar" onClick={onFechar} aria-label="Fechar">×</button>

        <img className="qf-arte" src={inteiro} alt="" aria-hidden="true" draggable={false} />

        <h2 className="qf-titulo" id="qf-titulo">Finn</h2>
        <p className="qf-linha">Seu assistente financeiro</p>

        <p className="qf-sobre">
          Uma capivara roxa que entende de dinheiro e te ajuda a ter um futuro
          financeiro melhor.
        </p>

        {/*
          O princípio, destacado — é ele que separa este app dos outros, e é a
          coisa que alguém precisa saber antes de decidir se confia no bicho.
        */}
        <p className="qf-principio">
          O Finn não diz o que você deve fazer com o seu dinheiro.
          Ele te ajuda a entender o que as suas escolhas significam.
        </p>

        <ul className="qf-faz">
          {FAZ.map(f => (
            <li key={f.titulo}>
              <strong>{f.titulo}</strong>
              <span>{f.texto}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default QuemEhOFinn;
